import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
    keepPreviousData
} from '@tanstack/react-query';
import {
    createUserWithWordpress,
    deleteUserWithWordpress,
    fetchUserByEmail,
    fetchUsers,
    updateUser,
    sendPasswordReset as sendPasswordResetRequest,
    type CreateUserInput,
    type UpdateUserInput,
    type UsersListMeta,
    type UsersListParams
} from '../services/users';
import { ACCOUNTS_USERS_KEY, PRACTICE_USERS_BASE_KEY } from './queryKeys';

const EMPTY_USERS_META: UsersListMeta = { total: 0, page: 1, limit: 0, totalPages: 1 };

export function useUsersList(params: UsersListParams & { enabled?: boolean }) {
    const queryKey = [
        ACCOUNTS_USERS_KEY,
        'list',
        params.page,
        params.limit,
        params.search ?? '',
        params.sort ?? '',
        params.order ?? '',
        (params.practice ?? []).join(','),
        params.role ?? ''
    ];

    const { data, isLoading, isFetching, isError, error } = useQuery({
        queryKey,
        queryFn: () => fetchUsers(params),
        staleTime: 60_000,
        placeholderData: keepPreviousData,
        enabled: params.enabled !== false
    });

    return {
        users: data?.data ?? [],
        meta: data?.meta ?? { ...EMPTY_USERS_META, page: params.page, limit: params.limit },
        isLoading,
        isFetching,
        isError,
        error
    };
}

/**
 * Same endpoint, accumulated instead of sliced - `DataTable`'s `'infinite'`
 * mode. The query key omits `page` and is nested under `ACCOUNTS_USERS_KEY`,
 * so the create/update/delete mutations' `invalidateQueries({ queryKey:
 * [ACCOUNTS_USERS_KEY] })` below already reaches it by prefix - no separate
 * invalidation needed (same reasoning as useInfinitePractices).
 */
export function useInfiniteUsers(params: Omit<UsersListParams, 'page'> & { enabled?: boolean }) {
    const { enabled = true, ...rest } = params;
    const {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        isError,
        error,
        hasNextPage,
        fetchNextPage
    } = useInfiniteQuery({
        queryKey: [
            ACCOUNTS_USERS_KEY,
            'infinite',
            rest.limit,
            rest.search ?? '',
            rest.sort ?? '',
            rest.order ?? '',
            (rest.practice ?? []).join(','),
            rest.role ?? ''
        ],
        initialPageParam: 1,
        queryFn: ({ pageParam }) => fetchUsers({ ...rest, page: pageParam as number }),
        getNextPageParam: (lastPage) =>
            lastPage.meta.page * lastPage.meta.limit < lastPage.meta.total
                ? lastPage.meta.page + 1
                : undefined,
        staleTime: 60_000,
        placeholderData: keepPreviousData,
        enabled
    });

    const firstPageMeta = data?.pages[0]?.meta;

    return {
        users: data?.pages.flatMap((page) => page.data) ?? [],
        meta: firstPageMeta ?? { ...EMPTY_USERS_META, limit: rest.limit },
        isLoading,
        isFetching,
        isError,
        error,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage
    };
}

// Runs the full WP -> DB -> optional-reset sequence (see
// createUserWithWordpress) so callers get one mutation for the entire
// "add a user" flow, matching internal's Users.tsx handleSave.
export function useCreateUser() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            input,
            sendPasswordReset
        }: {
            input: CreateUserInput;
            sendPasswordReset?: boolean;
        }) => createUserWithWordpress(input, { sendPasswordReset }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [ACCOUNTS_USERS_KEY] });
        }
    });
}

export function useUpdateUser() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ email, patch }: { email: string; patch: UpdateUserInput }) =>
            updateUser(email, patch),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [ACCOUNTS_USERS_KEY] });
            // A save from the drawer can change which practices this user is
            // linked to - invalidate every practice's Users tab (cheap,
            // unpaginated lists) rather than tracking which ones changed.
            queryClient.invalidateQueries({ queryKey: PRACTICE_USERS_BASE_KEY });
        }
    });
}

// Runs the full DB -> WP delete sequence (see deleteUserWithWordpress), so a
// non-404 WP cleanup failure is reported back on the result rather than
// thrown - the DB user is already gone by that point.
export function useDeleteUser() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (email: string) => deleteUserWithWordpress(email),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [ACCOUNTS_USERS_KEY] });
            // A deleted user must disappear from every practice's Users tab
            // it was linked to - same rationale as useUpdateUser above.
            queryClient.invalidateQueries({ queryKey: PRACTICE_USERS_BASE_KEY });
        }
    });
}

export function useSendPasswordReset() {
    return useMutation({
        mutationFn: (email: string) => sendPasswordResetRequest(email)
    });
}

// Backs the Users list's `?user=<email>` deep-link case: the row isn't on
// the currently-loaded page (or the page hasn't loaded yet), so the drawer
// needs the single-user GET directly. Returns the raw UserRecord shape
// (practices as ids only - see services/users.ts) - callers resolve names
// separately, e.g. via usePracticeNames.
export function useUserByEmail(email: string | undefined) {
    return useQuery({
        queryKey: [ACCOUNTS_USERS_KEY, 'byEmail', email ?? ''],
        queryFn: () => fetchUserByEmail(email as string),
        enabled: Boolean(email),
        retry: false,
        staleTime: 60_000
    });
}
